import * as React from 'react';
import {Helmet} from 'react-helmet';
import {useDispatch} from 'react-redux';
// @ts-expect-error
import type {RouterHistory} from 'react-router-dom';

import classNames from 'classnames';

import {api} from 'nutshell-core/api';
import type {User} from 'nutshell-core/users/users-types';

import {headTitle} from 'shells/document/head-title';
import {LoadingPage} from 'shells/loading-page/loading-page';

import {PageWrapper} from '../../layout/page-wrapper';
import {setAgentContext} from '../../team-ai/redux/agent-reducer';
import {LEAD_PAGE} from '../../team-ai/redux/consts';

import {Header} from '../header/header';
import {LeadLimitUpsellModal} from '../shared/components';
import {useGetLead, useGetLeadTags} from '../shared/graphql/hooks';
import {useUpdateLeadHandlers, useGetLeadMarkup} from '../shared/hooks';

import {LeadBackboneWatcher} from './backbone-watcher/lead-backbone-watcher';
import {ContentM1} from './content-m1';
import {Header as DeprecatedHeader} from './full-width-header/header';
import {SidebarM1} from './sidebar-m1';

import styles from './lead-page-m1.module.css';

type Props = {
    leadId?: string;
    leadNum?: number;
    history: RouterHistory;
    user: User;
};

export function LeadPageM1(props: Props) {
    const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
    const hasViewedLead = React.useRef<boolean>(false);

    const {
        lead,
        isLoading: isLoadingLead,
        error: errorMessage,
        refetch: refetchLead,
    } = useGetLead(props.leadId, props.leadNum);
    const {fetchMore: fetchMoreTags} = useGetLeadTags();

    const {
        handleUpdateName,
        handleUpdateConfidence,
        handleUpdateDueDate,
        handleUpdateCreatedDate,
        handleUpdateClosedDate,
        handleUpdateTags,
    } = useUpdateLeadHandlers(lead);

    const {
        pipelineTasks,
        pipelineTasksCount,
        setDidRenderPipelineTasks,
        setDidRenderCustomFields,
        customFields,
        customFieldsCount,
        sidebarCustomFields,
        sidebarCustomFieldsCount,
        setDidRenderSidebarCustomFields,
    } = useGetLeadMarkup((lead && lead.id) || props.leadId, (lead && lead.number) || props.leadNum);

    const dispatch = useDispatch();

    React.useEffect(() => {
        if (lead && !hasViewedLead.current) {
            api.post(`views/${lead.id}`, {}).then(() => {
                hasViewedLead.current = true;
            });
        }
    }, [lead]);

    // Set the AI agent context for the lead page
    React.useEffect(() => {
        if (lead) {
            dispatch(setAgentContext(lead.id, LEAD_PAGE, lead.name));

            return () => {
                dispatch(setAgentContext(null, null, null));
            };
        }
    }, [lead, dispatch]);

    if (
        errorMessage === 'Lead limit exceeded' // Beware of changing- we string match an error from the back-end here
    ) {
        return <LeadLimitUpsellModal history={props.history} />;
    }

    if (!lead || isLoadingLead) {
        return (
            <PageWrapper>
                <Helmet>
                    <title>{headTitle('Lead')}</title>
                </Helmet>
                <div className='absolutely-center-and-fill'>
                    <LoadingPage />
                </div>
            </PageWrapper>
        );
    }

    const hasLeadPageRedesignM3 = NutClientConfig.hasLeadPageRedesignM3FF;
    const contentContainerStyleName = classNames(styles['content-container'], {
        [styles['sidebar-and-content-container-scroll']]: hasLeadPageRedesignM3,
    });
    const contentScrollContainerStyleName = classNames({
        [styles['center-content-scroll-container']]: hasLeadPageRedesignM3,
    });

    const PageWrapperComponent = hasLeadPageRedesignM3 ? React.Fragment : PageWrapper;
    const ContentComponent = (
        <ContentM1
            lead={lead}
            stagesetId={lead.stageset && lead.stageset.id}
            pipelineTasks={pipelineTasks}
            pipelineTasksCount={pipelineTasksCount}
            onRenderPipelineTasks={() => {
                setDidRenderPipelineTasks(true);
            }}
            onRenderCustomFields={() => {
                setDidRenderCustomFields(true);
            }}
            customFields={customFields}
            customFieldsCount={customFieldsCount}
            scrollContainerRef={scrollContainerRef.current}
            user={props.user}
        />
    );

    return (
        <PageWrapperComponent>
            <Helmet>
                <title>{headTitle(['Lead', lead.name])}</title>
            </Helmet>
            <LeadBackboneWatcher lead={lead} />
            <div
                ref={(ref) => {
                    if (!NutClientConfig.hasLeadPageRedesignM3FF) {
                        scrollContainerRef.current = ref;
                    }
                }}
                className={styles.container}
            >
                <div>
                    {hasLeadPageRedesignM3 ? (
                        <Header
                            lead={lead}
                            onUpdateLeadName={handleUpdateName}
                            refetchLead={refetchLead}
                            onUpdateTags={handleUpdateTags}
                            fetchTags={fetchMoreTags}
                            onUpdateLeadConfidence={handleUpdateConfidence}
                            onUpdateLeadDueDate={handleUpdateDueDate}
                            onUpdateLeadCreatedDate={handleUpdateCreatedDate}
                            onUpdateLeadClosedDate={handleUpdateClosedDate}
                        />
                    ) : (
                        <DeprecatedHeader
                            lead={lead}
                            onUpdateLeadName={handleUpdateName}
                            onUpdateLeadConfidence={handleUpdateConfidence}
                            onUpdateLeadDueDate={handleUpdateDueDate}
                            onUpdateLeadCreatedDate={handleUpdateCreatedDate}
                            onUpdateLeadClosedDate={handleUpdateClosedDate}
                            fetchTags={fetchMoreTags}
                        />
                    )}
                </div>
                <div className={contentContainerStyleName}>
                    {hasLeadPageRedesignM3 ? (
                        <div
                            ref={(ref) => {
                                scrollContainerRef.current = ref;
                            }}
                            className={contentScrollContainerStyleName}
                        >
                            {ContentComponent}
                        </div>
                    ) : (
                        ContentComponent
                    )}
                    <SidebarM1
                        history={props.history}
                        lead={lead}
                        stagesetId={lead.stageset && lead.stageset.id}
                        sidebarCustomFields={sidebarCustomFields}
                        sidebarCustomFieldsCount={sidebarCustomFieldsCount}
                        onRenderSidebarCustomFields={() => {
                            setDidRenderSidebarCustomFields(true);
                        }}
                        user={props.user}
                    />
                </div>
            </div>
        </PageWrapperComponent>
    );
}
